/**
 * Logger utility that only outputs to console in development mode
 * This prevents logs from appearing in production builds
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

class Logger {
  private isDev: boolean;

  constructor() {
    // Environment is typically set by webpack/build process
    this.isDev = process.env.NODE_ENV !== 'production';
  }

  /**
   * Log a message (only in development mode)
   */
  log(...args: any[]): void {
    if (this.isDev) {
      console.log(...args);
    }
  }

  /**
   * Log info message (only in development mode)
   */
  info(...args: any[]): void {
    if (this.isDev) {
      console.info(...args);
    }
  }

  /**
   * Log warning message (only in development mode)
   */
  warn(...args: any[]): void {
    if (this.isDev) {
      console.warn(...args);
    }
  }

  /**
   * Log error message (only in development mode)
   */
  error(...args: any[]): void {
    if (this.isDev) {
      console.error(...args);
    }
  }

  /**
   * Log debug message (only in development mode)
   */
  debug(...args: any[]): void {
    if (this.isDev) {
      console.debug(...args);
    }
  }

  /**
   * Log table data (only in development mode)
   */
  table(tabularData?: any, properties?: string[]): void {
    if (this.isDev) {
      console.table(tabularData, properties);
    }
  }

  /**
   * Group logs (only in development mode)
   */
  group(label?: string): void {
    if (this.isDev) {
      console.group(label);
    }
  }

  /**
   * End log group (only in development mode)
   */
  groupEnd(): void {
    if (this.isDev) {
      console.groupEnd();
    }
  }

  /**
   * Time measurement start (only in development mode)
   */
  time(label?: string): void {
    if (this.isDev) {
      console.time(label);
    }
  }

  /**
   * Time measurement end (only in development mode)
   */
  timeEnd(label?: string): void {
    if (this.isDev) {
      console.timeEnd(label);
    }
  }
}

// Export as singleton instance
export const logger = new Logger();

// Also export as default
export default logger; 